import { useState, useEffect, useRef } from 'react';

interface Heading {
  id: string;
  text: string;
  level: 2 | 3;
}

interface Props {
  content: string;
}

export default function TableOfContents({ content }: Props) {
  const [activeId, setActiveId] = useState('');
  const [headings, setHeadings] = useState<Heading[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const els = Array.from(doc.querySelectorAll('h2[id], h3[id]'));
    setHeadings(
      els.map(el => ({
        id: el.getAttribute('id') || '',
        text: (el.textContent || '').trim(),
        level: el.tagName === 'H2' ? 2 : 3,
      }))
    );
  }, [content]);

  useEffect(() => {
    if (headings.length === 0) return;
    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-10% 0% -75% 0%', threshold: 0 }
    );

    headings.forEach(h => {
      const el = document.getElementById(h.id);
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [headings]);

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (headings.length === 0) return null;

  return (
    <nav
      aria-label="Índice do artigo"
      className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-6 max-h-[72vh] overflow-y-auto"
    >
      <p className="font-body text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-5 flex items-center gap-2">
        <i className="ri-list-check-2 text-primary text-sm"></i>
        Neste artigo
      </p>
      <ul className="space-y-2">
        {headings.map(h => (
          <li key={h.id} className={h.level === 3 ? 'pl-4' : ''}>
            <a
              href={`#${h.id}`}
              onClick={scrollTo(h.id)}
              className={`
                block font-body leading-snug transition-all duration-150 cursor-pointer
                ${h.level === 3 ? 'text-xs' : 'text-sm'}
                ${
                  activeId === h.id
                    ? 'text-primary font-semibold'
                    : 'text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary'
                }
              `}
            >
              {h.level === 3 && (
                <span className="text-gray-300 dark:text-gray-600 mr-1">–</span>
              )}
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
